import { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import { checkout } from "@/api/billing";
import IconLock from "@/components/icons/IconLock";
import { CardElement, useRecurly } from "@recurly/react-recurly";
import { RecurlyPlan } from "@/lib/recurly";
import { CANADA_PROVINCES, OTHER_COUNTRIES, TOP_COUNTRIES, US_STATES } from "@/lib/geo-data";
import Button from "@/components/forms/Button/Button";
import { useLocation } from "wouter";
import { RecurlyError } from "@recurly/recurly-js";
import { useUserContext } from "@/contexts/UserContext";
import { UserStatus } from "@boxem/core-api-schema";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";

import styles from "./PaymentInformationForm.module.css";
import { NotificationType, useNotificationContext } from "@/contexts/NotificationContext";

export default function PaymentInformationForm({ plan }: { plan: RecurlyPlan }) {
  const [location, navigate] = useLocation();
  const formRef = useRef(null);
  const recurly = useRecurly();
  const { setUser, user } = useUserContext();
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState("US");
  const { showNotification } = useNotificationContext();

  const handleChangeCountry = (event: ChangeEvent<HTMLSelectElement>) => {
    setCountry(event.target.value);
  }

  const handleError = (err: RecurlyError) => {
    const description = () => {
      let desc = "";

      err.details.map((det, index) => {
        if (det.field === "cvv") {
          return ""; //Return empty string for CVV because the message would be redundant.
        } else if (det.field === "number") {
          desc = desc + `card ${det.field} ${det.messages[0]}`;
        } else if (index > 0) {
          desc = desc + `, ${det.field} ${det.messages[0]}`;
        } else {
          desc = desc + ` ${det.field} ${det.messages[0]}`;
        }
      });

      return desc;
    };

    showNotification(err.message + description(), NotificationType.ERROR);
  };

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);

    if (!formRef.current) {
      return;
    }

    const formElement = formRef.current;

    recurly.token(formElement, async (err, token) => {
      if (err) {
        console.error(err);
        handleError(err);
        setLoading(false);
        return;
      }

      try {
        const formData = new FormData(formElement);
        const response = await checkout({
          firstName: formData.get("first_name").toString(),
          lastName: formData.get("last_name").toString(),
          recurlyTokenId: token.id,
          planCode: plan.code,
        });

        showNotification("Successfully subscribed.", NotificationType.SUCCESS);

        navigate("~/");
        setUser({ ...user, status: user.status === UserStatus.INCOMPLETE ? UserStatus.EMAIL_VERIFICATION : user.status }, false);

      } catch (err: any) {
        console.error('Subscription failed', err);
        const message = `Subscription failed.${err.message ? `\n${err.message}` : "An unknown error occurred."}`;
        showNotification(message, NotificationType.ERROR);
      } finally {
        setLoading(false);
      }
    });
  };

  useEffect(() => {
    if (!user.emailVerified) {
      navigate(`~/auth/verify-email?plan=${plan}`);
    }
  }, [user]);

  return (
    <form ref={formRef} onSubmit={onSubmit} className={`${styles.form}`}>
      <div className={styles.space}></div>
      <div className={styles.row}>
        <label>
          First Name
          <input name="first_name" data-recurly="first_name" placeholder="John" />
        </label>
        <label>
          Last Name
          <input name="last_name" data-recurly="last_name" placeholder="Doe" />
        </label>
      </div>
      <div className={styles.space}></div>
      <label>
        Card Number
        <CardElement
          style={{
            fontSize: "16px",
            fontFamily: "Inter",
            fontColor: "var(--static-text-strong)",
          }}
        />
      </label>
      <div className={styles.divider}></div>
      <label>
        Country of residence
        <select className={styles.fw} name="country" required data-recurly="country"
          value={country}
          onChange={handleChangeCountry}
        >
          <option disabled value={null}>
            Select country
          </option>
          {TOP_COUNTRIES.map(({ Code, Name }, i) => (
            <option key={i} value={Code}>{Name}</option>
          ))}
          <hr />
          {OTHER_COUNTRIES.map(({ Code, Name }, i) => (
            <option key={i} value={Code}>{Name}</option>
          ))}

        </select>
      </label>

      <div className={styles.space}></div>
      <label>
        Street Address
        <input name="address1" required data-recurly="address1" placeholder="Street Address" />
      </label>
      <div className={styles.space}></div>

      <label>
        Apartment / Suite number
        <input name="address2" data-recurly="address2" placeholder="Enter apartment / suite number" />
      </label>
      <div className={styles.space}></div>

      <label>
        City
        <input name="city" required data-recurly="city" placeholder="Enter city" />
      </label>
      <div className={styles.space}></div>
      <div className={styles.row}>
        <label>
          {country === 'US' ? 'State' : 'Province'}
          <select name="state" required data-recurly="state">
            <option disabled value={null}>
              Select state
            </option>
            {country === 'US' && US_STATES.map((state) => (
              <option key={state} value={state}>{state}</option>
            ))}
            {country === 'CA' && CANADA_PROVINCES.map((state) => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
        </label>
        <label>
          ZIP
          <input name="postal_code" required data-recurly="postal_code" placeholder="00000" />
        </label>
      </div>

      <div className={styles.divider}></div>
      <div className={styles.encrypted}>
        <IconLock />
        This is a secure 256-bit SSL encrypted payment
      </div>
      <div className={styles.space}></div>
      <div className={styles.navigateInline}>
        By clicking Complete you are agreeing to our <a href="https://www.boxem.com/termsandconditions" target="_blank">Terms & Conditions</a> and have read
        through our <a href="https://www.boxem.com/privacypolicy" target="_blank">Privacy Policy</a>.
      </div>
      <div className={styles.space}></div>
      <Button htmlType="submit" type="primary" size="large" disabled={loading} block>
        {loading ? <LoadingSpinner /> : <span>Complete</span>}
      </Button>
    </form>
  );
}
